import * as React from "react";
import * as ReactRouter from "react-router-dom";
import * as Redux from "react-redux";

import useContentApi from "../../hooks/useContentApi";
import {TopicEntity} from "../../contract/topic";
import {RootState} from "../../redux/store";
import {Tag} from "../Content";

interface Props {
  size: number;
  compact?: boolean;
  slug?: string;
  label?: string;
  priority?: number;
  className?: string;
  final?: boolean;
  style?: { [key: string]: number | string }
}

const ArticleTopicContent: React.FC<Props> = (
  {
    compact,
    slug,
    label,
    priority = 0,
    final = false,
    ...props
  }
): React.ReactElement => {
  const navigate = ReactRouter.useNavigate();
  const contentApi = useContentApi();

  const {base, topic}: { base: string, topic: TopicEntity } = Redux.useSelector((state: RootState) => {
    const topics = Object.values(state.entities.topics.byId);
    const {config} = state.application;

    return {
      base: config.base,
      topic: (topics || []).find((node: TopicEntity) => node.slug === slug)
    }
  });

  const getLink: (slug: string) => string = (slug) => slug && `${base}/${slug}`;

  React.useEffect(() => {
    if (compact || !topic || !contentApi) return;

    let active = true;

    (async () => {
      try {
        if (priority > 0) {
          const result = await contentApi.queue.add(
            () => contentApi.getTopicContent(topic.slug),
            {id: topic.slug, priority}
          );

          if (!active || !result) return;

          return Array.isArray(result) ? result[0] : result;
        }
      } catch (e) {
        return null;
      }
    })().then(result => {
      if (!result) return;
      if (!result.id) throw new Error(`Malformed response for ${topic.slug}`);
    });

    return () => {
      active = false;
      contentApi.cancel(String(topic?.id));
    }
  }, [topic, priority]); // eslint-disable-line react-hooks/exhaustive-deps

  return !compact ? (
    <Tag
      onClick={(e: React.SyntheticEvent): void => {
        e.preventDefault();
        navigate(getLink(String(slug)));
      }}

      {...props}
      className={`${props.className} is-topic`}
    >
      <label>{label}</label>
    </Tag>
  ) : (
    <span style={{marginRight: !final ? '2px' : 0}}>{label}{!final ? ', ' : ''}</span>
  );
};

export default ArticleTopicContent;
